import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import {
  DATA_TRANSFER_LIST_POLL_INTERVAL_MS,
  DATA_TRANSFER_RUN_DETAIL_POLL_INTERVAL_MS,
  pollingOptions,
} from 'src/constants/polling.constants';
import {
  PortalDsButton,
  PortalDsCard,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import { formatDataTransferApiError } from '../helpers/data-transfers.helpers';
import {
  useCreateClientDataTransferMutation,
  useDeleteClientDataTransferMutation,
  useGetClientDataTransfersQuery,
  useGetDataTransferRunQuery,
  useUpdateClientDataTransferMutation,
} from 'src/redux/operations/operations.endpoints';
import type {
  ClientDataTransfer,
  ClientDataTransferWritePayload,
  DataTransferRun,
} from '../types/data-transfers.types';
import { ClientDataTransferDetailSheet } from './client-data-transfer-detail-sheet';
import { ClientDataTransferSheet, type ClientDataTransferSheetMode } from './client-data-transfer-sheet';
import { ClientTransfersByClientList } from './client-transfers-by-client-list';
import { DataTransferRunSheet } from './data-transfer-run-sheet';

type TransferSheetState =
  | { open: false }
  | { open: true; mode: ClientDataTransferSheetMode; transfer: ClientDataTransfer | null };

export function DataTransfersSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTransferId = searchParams.get('transfer');
  const selectedRunId = searchParams.get('run');

  const [transferSheet, setTransferSheet] = useState<TransferSheetState>({ open: false });
  const [detailTransfer, setDetailTransfer] = useState<ClientDataTransfer | null>(null);
  const [transferSubmitError, setTransferSubmitError] = useState<string | null>(null);
  const [transferDeleteError, setTransferDeleteError] = useState<string | null>(null);

  const {
    data: transfers = [],
    isLoading: isLoadingTransfers,
    isFetching: isFetchingTransfers,
    refetch: refetchTransfers,
  } = useGetClientDataTransfersQuery(undefined, pollingOptions(DATA_TRANSFER_LIST_POLL_INTERVAL_MS));

  const [createTransfer, { isLoading: isCreatingTransfer }] = useCreateClientDataTransferMutation();
  const [updateTransfer, { isLoading: isUpdatingTransfer }] = useUpdateClientDataTransferMutation();
  const [deleteTransfer, { isLoading: isDeletingTransfer }] = useDeleteClientDataTransferMutation();

  const {
    data: selectedRun,
    isLoading: isRunLoading,
    refetch: refetchRun,
  } = useGetDataTransferRunQuery(
    { runId: selectedRunId ?? '' },
    {
      skip: !selectedRunId,
      ...pollingOptions(DATA_TRANSFER_RUN_DETAIL_POLL_INTERVAL_MS),
      pollingInterval: selectedRunId ? DATA_TRANSFER_RUN_DETAIL_POLL_INTERVAL_MS : 0,
    }
  );

  useEffect(() => {
    if (!selectedTransferId) {
      setDetailTransfer(null);
      return;
    }
    const transfer = transfers.find((item) => String(item.id) === selectedTransferId) ?? null;
    setDetailTransfer(transfer);
  }, [selectedTransferId, transfers]);

  useEffect(() => {
    if (selectedRunId && selectedRun && selectedRun.id !== selectedRunId) {
      refetchRun();
    }
  }, [refetchRun, selectedRun, selectedRunId]);

  const openCreateTransfer = () => {
    setTransferSubmitError(null);
    setTransferDeleteError(null);
    setTransferSheet({ open: true, mode: 'create', transfer: null });
  };

  const openEditTransfer = (transfer: ClientDataTransfer) => {
    setTransferSubmitError(null);
    setTransferDeleteError(null);
    setTransferSheet({ open: true, mode: 'edit', transfer });
  };

  const openTransferDetail = (transfer: ClientDataTransfer) => {
    const next = new URLSearchParams(searchParams);
    next.set('transfer', String(transfer.id));
    next.delete('run');
    setSearchParams(next, { replace: true });
  };

  const closeTransferDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('transfer');
    next.delete('run');
    setSearchParams(next, { replace: true });
  };

  const openRunDetail = (run: DataTransferRun) => {
    const next = new URLSearchParams(searchParams);
    next.set('transfer', String(run.transfer));
    next.set('run', run.id);
    setSearchParams(next, { replace: true });
  };

  const closeRunDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('run');
    setSearchParams(next, { replace: true });
  };

  const handleTransferSubmit = async(
    payload: ClientDataTransferWritePayload
  ) => {
    try {
      if (!transferSheet.open) {
        return;
      }
      if (transferSheet.mode === 'create') {
        const created = await createTransfer(payload).unwrap();
        setTransferSubmitError(null);
        setTransferSheet({ open: true, mode: 'edit', transfer: created });
        return;
      }
      if (!transferSheet.transfer) {
        return;
      }
      const updated = await updateTransfer({
        transferId: transferSheet.transfer.id,
        body: payload,
      }).unwrap();
      setTransferSubmitError(null);
      setTransferSheet({ open: true, mode: 'edit', transfer: updated });
    } catch (error) {
      setTransferSubmitError(formatDataTransferApiError(error, 'Could not save the transfer.'));
    }
  };

  const handleTransferDelete = async() => {
    if (!transferSheet.open || transferSheet.mode !== 'edit' || !transferSheet.transfer) {
      return;
    }
    try {
      await deleteTransfer({ transferId: transferSheet.transfer.id }).unwrap();
      setTransferDeleteError(null);
      setTransferSheet({ open: false });
    } catch (error) {
      setTransferDeleteError(formatDataTransferApiError(error, 'Could not delete the transfer.'));
      throw error;
    }
  };

  return (
    <div className={portalDs.page}>
      <PortalDsCard>
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <PortalDsSectionHeader
            title="Client data transfers"
            description="Per-client BigQuery SQL jobs grouped by client, with live status, run history, and quick actions."
            className="mb-0"
          />
          <div className="flex flex-wrap gap-2">
            <PortalDsButton
              type="button"
              variant="ghost"
              onClick={() => refetchTransfers()}
            >
              <RefreshCw className={`mr-1 size-4 ${isFetchingTransfers ? 'animate-spin' : ''}`} />
              Refresh
            </PortalDsButton>
            <PortalDsButton
              type="button"
              onClick={openCreateTransfer}
            >
              <Plus className="mr-1 size-4" />
              New transfer
            </PortalDsButton>
          </div>
        </div>

        <ClientTransfersByClientList
          transfers={transfers}
          isLoading={isLoadingTransfers}
          onSelectTransfer={openTransferDetail}
          onEditTransfer={openEditTransfer}
        />
      </PortalDsCard>

      <ClientDataTransferSheet
        open={transferSheet.open}
        mode={transferSheet.open ? transferSheet.mode : 'create'}
        transfer={transferSheet.open ? transferSheet.transfer : null}
        isSaving={isCreatingTransfer || isUpdatingTransfer}
        isDeleting={isDeletingTransfer}
        submitError={transferSubmitError}
        deleteError={transferDeleteError}
        onOpenChange={(open) => {
          if (!open) {
            setTransferSheet({ open: false });
          }
        }}
        onSubmit={handleTransferSubmit}
        onDelete={transferSheet.open && transferSheet.mode === 'edit' ? handleTransferDelete : undefined}
      />

      <ClientDataTransferDetailSheet
        open={Boolean(selectedTransferId && detailTransfer)}
        transfer={detailTransfer}
        onOpenChange={(open) => {
          if (!open) {
            closeTransferDetail();
          }
        }}
        onEdit={openEditTransfer}
        onSelectRun={openRunDetail}
      />

      <DataTransferRunSheet
        open={Boolean(selectedRunId)}
        run={selectedRun}
        isLoading={isRunLoading}
        onOpenChange={(open) => {
          if (!open) {
            closeRunDetail();
          }
        }}
      />
    </div>
  );
}
